import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  CreateFlightPayload,
  Flight,
  FlightListFilter,
  FlightResponse,
  FlightStatusPayload,
} from "../types/flights.types";

type FlightListResponse = FlightResponse;

type FlightMutationResponse = {
  statusCode: number;
  success: boolean;
  message: string;
  resData: unknown;
};

type FlightDetailResponse = {
  statusCode: number;
  success: boolean;
  message: string;
  resData: Flight;
};

type UpdateFlightPayload = Partial<CreateFlightPayload>;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const flightsService = {
  async list(filter: FlightListFilter): Promise<FlightListResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<FlightListResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.list(params.toString()),
        { method: "GET", isAuthenticated: true, cache: "no-store" },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load flights due to an unexpected error.",
      ) as unknown as FlightListResponse;
    }
  },

  async create(payload: CreateFlightPayload): Promise<FlightMutationResponse> {
    try {
      return await apiClient<FlightMutationResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.create,
        {
          method: "POST",
          body: JSON.stringify(payload),
          isAuthenticated: true,
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to create flight due to an unexpected error.",
      ) as FlightMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: UpdateFlightPayload,
  ): Promise<FlightMutationResponse> {
    try {
      return await apiClient<FlightMutationResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.update(id),
        {
          method: "PATCH",
          body: JSON.stringify(payload),
          isAuthenticated: true,
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to update flight due to an unexpected error.",
      ) as FlightMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: FlightStatusPayload,
  ): Promise<FlightMutationResponse> {
    try {
      return await apiClient<FlightMutationResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.delete(id),
        {
          method: "DELETE",
          body: JSON.stringify(payload),
          isAuthenticated: true,
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to change flight status due to an unexpected error.",
      ) as FlightMutationResponse;
    }
  },

  async details(id: number | string): Promise<FlightDetailResponse> {
    try {
      return await apiClient<FlightDetailResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.details(id),
        { method: "GET", isAuthenticated: true, cache: "no-store" },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load flight details due to an unexpected error.",
      ) as unknown as FlightDetailResponse;
    }
  },

  async delete(id: number | string): Promise<FlightMutationResponse> {
    try {
      return await apiClient<FlightMutationResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.flights.delete(id),
        { method: "DELETE", isAuthenticated: true },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to delete flight due to an unexpected error.",
      ) as FlightMutationResponse;
    }
  },
};
